import { Injectable, signal } from '@angular/core';
import { ImageSegmenter, FilesetResolver, ImageSegmenterResult } from '@mediapipe/tasks-vision';

// Both are served from our own origin instead of a CDN: angular.json copies
// the WASM runtime out of node_modules/@mediapipe/tasks-vision (so the version
// is the one in the lockfile), and the model is vendored under
// src/assets/mediapipe (see the README there for provenance). Resolved against
// the document base URL so direct room links load the same assets.
const MEDIAPIPE_WASM_BASE_URL = new URL('assets/mediapipe/wasm', document.baseURI).href;
const SELFIE_SEGMENTER_MODEL_URL = new URL(
  'assets/mediapipe/selfie_segmenter.tflite',
  document.baseURI
).href;

export type BlurState = 'idle' | 'loading' | 'ready' | 'processing' | 'error';

/**
 * Detect if the current browser/platform has a known issue with captureStream()
 * Chromium-based browsers on Linux have a bug where captureStream() produces blank frames
 */
function hasKnownCaptureStreamIssue(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = (navigator.platform || '').toLowerCase();
  const uaPlatform = (navigator as any).userAgentData?.platform?.toLowerCase() ?? '';
  const isLinux =
    (userAgent.includes('linux') || platform.includes('linux') || uaPlatform.includes('linux')) &&
    !userAgent.includes('android');
  console.log('BackgroundBlurService: isLinux:', isLinux);
  const isChromium =
    userAgent.includes('chrome') ||
    userAgent.includes('chromium') ||
    userAgent.includes('brave') ||
    userAgent.includes('edge');
  console.log('BackgroundBlurService: isChromium:', isChromium);
  const isFirefox = userAgent.includes('firefox');

  // Firefox works fine, Chromium on Linux has issues
  return isLinux && isChromium && !isFirefox;
}

// Vertex shader - simple passthrough for fullscreen quad
const VERTEX_SHADER = `#version 300 es
in vec2 a_position;
in vec2 a_texCoord;
out vec2 v_texCoord;

void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_texCoord = a_texCoord;
}
`;

// Fragment shader for horizontal blur pass
const BLUR_H_SHADER = `#version 300 es
precision highp float;

in vec2 v_texCoord;
out vec4 fragColor;

uniform sampler2D u_texture;
uniform vec2 u_texelSize;
uniform float u_blurSize;

void main() {
  vec4 color = vec4(0.0);
  float total = 0.0;

  // 9-tap Gaussian blur (horizontal)
  for (int i = -4; i <= 4; i++) {
    float weight = exp(-0.5 * float(i * i) / 4.0);
    vec2 offset = vec2(float(i) * u_texelSize.x * u_blurSize, 0.0);
    color += texture(u_texture, v_texCoord + offset) * weight;
    total += weight;
  }

  fragColor = color / total;
}
`;

// Fragment shader for vertical blur pass
const BLUR_V_SHADER = `#version 300 es
precision highp float;

in vec2 v_texCoord;
out vec4 fragColor;

uniform sampler2D u_texture;
uniform vec2 u_texelSize;
uniform float u_blurSize;

void main() {
  vec4 color = vec4(0.0);
  float total = 0.0;

  // 9-tap Gaussian blur (vertical)
  for (int i = -4; i <= 4; i++) {
    float weight = exp(-0.5 * float(i * i) / 4.0);
    vec2 offset = vec2(0.0, float(i) * u_texelSize.y * u_blurSize);
    color += texture(u_texture, v_texCoord + offset) * weight;
    total += weight;
  }

  fragColor = color / total;
}
`;

// Fragment shader for final compositing (blend sharp person over blurred background)
const COMPOSITE_SHADER = `#version 300 es
precision highp float;

in vec2 v_texCoord;
out vec4 fragColor;

uniform sampler2D u_videoTexture;
uniform sampler2D u_blurredTexture;
uniform sampler2D u_maskTexture;

void main() {
  vec4 sharp = texture(u_videoTexture, v_texCoord);
  vec4 blurred = texture(u_blurredTexture, v_texCoord);
  float mask = texture(u_maskTexture, v_texCoord).r;

  // Blend: mask=1 means person (sharp), mask=0 means background (blurred)
  fragColor = mix(blurred, sharp, mask);
}
`;

interface ShaderProgram {
  program: WebGLProgram;
  uniforms: Record<string, WebGLUniformLocation | null>;
  attributes: Record<string, number>;
}

@Injectable({
  providedIn: 'root',
})
export class BackgroundBlurService {
  private segmenter: ImageSegmenter | null = null;
  private _state = signal<BlurState>('idle');
  readonly state = this._state.asReadonly();

  // Video element
  private sourceVideo: HTMLVideoElement | null = null;

  // WebGL resources
  private outputCanvas: HTMLCanvasElement | null = null;
  private gl: WebGL2RenderingContext | null = null;
  private blurHProgram: ShaderProgram | null = null;
  private blurVProgram: ShaderProgram | null = null;
  private compositeProgram: ShaderProgram | null = null;

  // Textures
  private videoTexture: WebGLTexture | null = null;
  private maskTexture: WebGLTexture | null = null;
  private blurTempTexture: WebGLTexture | null = null;
  private blurredTexture: WebGLTexture | null = null;

  // Framebuffers for multi-pass rendering
  private blurTempFBO: WebGLFramebuffer | null = null;
  private blurredFBO: WebGLFramebuffer | null = null;

  // Geometry
  private vao: WebGLVertexArrayObject | null = null;
  private positionBuffer: WebGLBuffer | null = null;
  private texCoordBuffer: WebGLBuffer | null = null;

  private timerWorker: Worker | null = null;
  private outputStream: MediaStream | null = null;
  private originalStream: MediaStream | null = null;
  private blurAmount = 2.0; // Blur strength multiplier
  private width = 640;
  private height = 360;
  private lastVideoTime = -1; // Track last processed video frame time

  /**
   * Initialize the MediaPipe ImageSegmenter with GPU acceleration
   */
  async initialize(): Promise<void> {
    if (this.segmenter) return;
    if (this._state() === 'loading') return;

    this._state.set('loading');

    try {
      const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_BASE_URL);

      // Try GPU first, fall back to CPU if needed
      try {
        this.segmenter = await ImageSegmenter.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: SELFIE_SEGMENTER_MODEL_URL,
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          outputCategoryMask: false,
          outputConfidenceMasks: true,
        });
        console.log('BackgroundBlurService: Segmenter initialized with GPU acceleration');
      } catch (gpuError) {
        console.warn('BackgroundBlurService: GPU delegate failed, falling back to CPU', gpuError);
        this.segmenter = await ImageSegmenter.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: SELFIE_SEGMENTER_MODEL_URL,
            delegate: 'CPU',
          },
          runningMode: 'VIDEO',
          outputCategoryMask: false,
          outputConfidenceMasks: true,
        });
        console.log('BackgroundBlurService: Segmenter initialized with CPU fallback');
      }

      this._state.set('ready');
    } catch (error) {
      console.error('BackgroundBlurService: Failed to initialize segmenter', error);
      this._state.set('error');
      throw error;
    }
  }

  /**
   * Apply background blur to a MediaStream and return a new processed MediaStream
   */
  async applyBlur(inputStream: MediaStream): Promise<MediaStream> {
    // Warn if platform has known issues
    if (hasKnownCaptureStreamIssue()) {
      console.warn(
        'BackgroundBlurService: Background blur may not work correctly on Chromium/Linux ' +
          'due to a known captureStream() bug. Consider using Firefox instead.'
      );
    }

    if (!this.segmenter) {
      await this.initialize();
    }

    if (!this.segmenter) {
      throw new Error('Segmenter not initialized');
    }

    this.originalStream = inputStream;
    this._state.set('processing');

    // Get video track settings
    const videoTrack = inputStream.getVideoTracks()[0];
    const settings = videoTrack.getSettings();
    this.width = settings.width || 640;
    this.height = settings.height || 360;

    // Create video element
    this.sourceVideo = document.createElement('video');
    this.sourceVideo.srcObject = inputStream;
    this.sourceVideo.muted = true;
    this.sourceVideo.playsInline = true;
    this.sourceVideo.autoplay = true;
    this.sourceVideo.width = this.width;
    this.sourceVideo.height = this.height;

    // Attach to DOM (hidden) - required by some browsers
    this.sourceVideo.style.position = 'fixed';
    this.sourceVideo.style.top = '-9999px';
    this.sourceVideo.style.left = '-9999px';
    this.sourceVideo.style.width = '1px';
    this.sourceVideo.style.height = '1px';
    this.sourceVideo.style.opacity = '0';
    this.sourceVideo.style.pointerEvents = 'none';
    document.body.appendChild(this.sourceVideo);

    // Create output canvas with WebGL2 context
    this.outputCanvas = document.createElement('canvas');
    this.outputCanvas.width = this.width;
    this.outputCanvas.height = this.height;
    this.outputCanvas.id = 'background-blur-output-canvas';
    this.outputCanvas.style.position = 'fixed';
    this.outputCanvas.style.top = '-9999px';
    this.outputCanvas.style.left = '-9999px';
    this.outputCanvas.style.pointerEvents = 'none';
    document.body.appendChild(this.outputCanvas);

    // Initialize WebGL
    this.initWebGL();

    // Wait for video to be ready
    await new Promise<void>((resolve, reject) => {
      if (!this.sourceVideo) {
        reject(new Error('Source video not initialized'));
        return;
      }
      this.sourceVideo.onloadedmetadata = () => {
        this.sourceVideo?.play().then(resolve).catch(reject);
      };
      this.sourceVideo.onerror = () => reject(new Error('Video loading failed'));
    });

    // Create output stream
    console.log('BackgroundBlurService: Using WebGL rendering with captureStream');
    this.outputStream = this.outputCanvas.captureStream(30);

    // Start processing
    this.lastVideoTime = -1;
    this.startProcessingLoop();

    // Handle track ending
    videoTrack.onended = () => {
      this.stopProcessing();
    };

    return this.outputStream;
  }

  /**
   * Initialize WebGL2 context and resources
   */
  private initWebGL(): void {
    if (!this.outputCanvas) return;

    this.gl = this.outputCanvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    });

    if (!this.gl) {
      throw new Error('WebGL2 not supported');
    }

    const gl = this.gl;

    // Handle context loss
    this.outputCanvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      console.warn('BackgroundBlurService: WebGL context lost');
      this.stopProcessing();
    });

    // Compile shader programs
    this.blurHProgram = this.createShaderProgram(gl, VERTEX_SHADER, BLUR_H_SHADER, [
      'u_texture',
      'u_texelSize',
      'u_blurSize',
    ]);
    this.blurVProgram = this.createShaderProgram(gl, VERTEX_SHADER, BLUR_V_SHADER, [
      'u_texture',
      'u_texelSize',
      'u_blurSize',
    ]);
    this.compositeProgram = this.createShaderProgram(gl, VERTEX_SHADER, COMPOSITE_SHADER, [
      'u_videoTexture',
      'u_blurredTexture',
      'u_maskTexture',
    ]);

    // Create VAO and geometry buffers
    this.vao = gl.createVertexArray();
    gl.bindVertexArray(this.vao);

    // Fullscreen quad positions (clip space)
    const positions = new Float32Array([-1, -1, -1, 1, 1, -1, 1, -1, -1, 1, 1, 1]);

    // Texture coordinates (flip Y for video)
    const texCoords = new Float32Array([0, 1, 0, 0, 1, 1, 1, 1, 0, 0, 1, 0]);

    this.positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.texCoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 0, 0);

    gl.bindVertexArray(null);

    // Create textures
    this.videoTexture = this.createTexture(gl);
    this.maskTexture = this.createFloatTexture(gl, this.width, this.height);
    this.blurTempTexture = this.createRenderTexture(gl, this.width, this.height);
    this.blurredTexture = this.createRenderTexture(gl, this.width, this.height);

    // Create framebuffers for multi-pass rendering
    this.blurTempFBO = this.createFramebuffer(gl, this.blurTempTexture);
    this.blurredFBO = this.createFramebuffer(gl, this.blurredTexture);

    console.log('BackgroundBlurService: WebGL initialized');
  }

  /**
   * Create and compile a shader program
   */
  private createShaderProgram(
    gl: WebGL2RenderingContext,
    vertexSrc: string,
    fragmentSrc: string,
    uniformNames: string[]
  ): ShaderProgram {
    const vertexShader = this.compileShader(gl, gl.VERTEX_SHADER, vertexSrc);
    const fragmentShader = this.compileShader(gl, gl.FRAGMENT_SHADER, fragmentSrc);

    const program = gl.createProgram()!;
    gl.attachShader(program, vertexShader!);
    gl.attachShader(program, fragmentShader!);

    // Bind attribute locations
    gl.bindAttribLocation(program, 0, 'a_position');
    gl.bindAttribLocation(program, 1, 'a_texCoord');

    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`Shader link error: ${gl.getProgramInfoLog(program)}`);
    }

    // Get uniform locations
    const uniforms: Record<string, WebGLUniformLocation | null> = {};
    for (const name of uniformNames) {
      uniforms[name] = gl.getUniformLocation(program, name);
    }

    return {
      program,
      uniforms,
      attributes: { a_position: 0, a_texCoord: 1 },
    };
  }

  /**
   * Compile a shader
   */
  private compileShader(
    gl: WebGL2RenderingContext,
    type: number,
    source: string
  ): WebGLShader | null {
    const shader = gl.createShader(type);
    if (!shader) return null;

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error(`Shader compile error: ${gl.getShaderInfoLog(shader)}`);
      gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  /**
   * Create a texture for sampling
   */
  private createTexture(gl: WebGL2RenderingContext): WebGLTexture | null {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return texture;
  }

  /**
   * Create a single-channel R32F texture for the segmentation mask
   */
  private createFloatTexture(
    gl: WebGL2RenderingContext,
    width: number,
    height: number
  ): WebGLTexture | null {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, width, height, 0, gl.RED, gl.FLOAT, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    // Use NEAREST filtering — R32F with LINEAR requires OES_texture_float_linear
    // which isn't universally available. NEAREST is sufficient for the segmentation mask.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    return texture;
  }

  /**
   * Create a texture for render-to-texture
   */
  private createRenderTexture(
    gl: WebGL2RenderingContext,
    width: number,
    height: number
  ): WebGLTexture | null {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return texture;
  }

  /**
   * Create a framebuffer for render-to-texture
   */
  private createFramebuffer(
    gl: WebGL2RenderingContext,
    texture: WebGLTexture | null
  ): WebGLFramebuffer | null {
    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return fbo;
  }

  /**
   * Main processing loop
   */
  /**
   * Create an inline Worker that ticks at a fixed interval.
   * Worker timers are not throttled in background tabs, unlike requestAnimationFrame.
   */
  private createTimerWorker(intervalMs: number): Worker {
    const blob = new Blob([`setInterval(() => postMessage(0), ${intervalMs})`], {
      type: 'text/javascript',
    });
    return new Worker(URL.createObjectURL(blob));
  }

  private startProcessingLoop(): void {
    // Use a Worker-based timer so processing continues in background tabs.
    // requestAnimationFrame is throttled to near-zero in background tabs,
    // which would freeze the captureStream output for remote participants.
    const intervalMs = 66; // ~15fps, matching camera ideal framerate
    this.timerWorker = this.createTimerWorker(intervalMs);

    this.timerWorker.onmessage = () => {
      if (!this.sourceVideo || !this.segmenter || !this.gl) {
        return;
      }

      if (
        this.sourceVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
        this.sourceVideo.currentTime !== this.lastVideoTime
      ) {
        this.lastVideoTime = this.sourceVideo.currentTime;
        const startTimeMs = performance.now();

        // Run segmentation
        const result = this.segmenter.segmentForVideo(this.sourceVideo, startTimeMs);

        // Render frame using WebGL
        this.renderFrameWebGL(result);

        // Close result to free resources
        result.close();
      }
    };
  }

  /**
   * Render a frame using WebGL (multi-pass blur + composite)
   */
  private renderFrameWebGL(result: ImageSegmenterResult): void {
    const gl = this.gl;
    if (
      !gl ||
      !this.sourceVideo ||
      !this.vao ||
      !this.blurHProgram ||
      !this.blurVProgram ||
      !this.compositeProgram ||
      !this.videoTexture ||
      !this.maskTexture ||
      !this.blurTempTexture ||
      !this.blurredTexture ||
      !this.blurTempFBO ||
      !this.blurredFBO
    ) {
      return;
    }

    const width = this.width;
    const height = this.height;

    // Upload video frame to texture
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.sourceVideo);

    // Upload mask directly as single-channel R32F texture (no conversion needed)
    const confidenceMasks = result.confidenceMasks;
    if (confidenceMasks && confidenceMasks.length > 0) {
      const maskData = confidenceMasks[0].getAsFloat32Array();

      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this.maskTexture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, width, height, 0, gl.RED, gl.FLOAT, maskData);
    }

    gl.bindVertexArray(this.vao);

    // === Pass 1: Horizontal blur (video -> blurTemp) ===
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.blurTempFBO);
    gl.viewport(0, 0, width, height);
    gl.useProgram(this.blurHProgram.program);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTexture);
    gl.uniform1i(this.blurHProgram.uniforms['u_texture'], 0);
    gl.uniform2f(this.blurHProgram.uniforms['u_texelSize'], 1.0 / width, 1.0 / height);
    gl.uniform1f(this.blurHProgram.uniforms['u_blurSize'], this.blurAmount);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // === Pass 2: Vertical blur (blurTemp -> blurred) ===
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.blurredFBO);
    gl.useProgram(this.blurVProgram.program);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.blurTempTexture);
    gl.uniform1i(this.blurVProgram.uniforms['u_texture'], 0);
    gl.uniform2f(this.blurVProgram.uniforms['u_texelSize'], 1.0 / width, 1.0 / height);
    gl.uniform1f(this.blurVProgram.uniforms['u_blurSize'], this.blurAmount);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // === Pass 3: Composite (video + blurred + mask -> screen) ===
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, width, height);
    gl.useProgram(this.compositeProgram.program);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTexture);
    gl.uniform1i(this.compositeProgram.uniforms['u_videoTexture'], 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.blurredTexture);
    gl.uniform1i(this.compositeProgram.uniforms['u_blurredTexture'], 1);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.maskTexture);
    gl.uniform1i(this.compositeProgram.uniforms['u_maskTexture'], 2);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.bindVertexArray(null);
  }

  /**
   * Stop processing
   */
  stopProcessing(): void {
    if (this.timerWorker) {
      this.timerWorker.terminate();
      this.timerWorker = null;
    }

    if (this.sourceVideo) {
      this.sourceVideo.srcObject = null;
      if (this.sourceVideo.parentNode) {
        this.sourceVideo.parentNode.removeChild(this.sourceVideo);
      }
      this.sourceVideo = null;
    }

    if (this.outputStream) {
      this.outputStream.getTracks().forEach((track) => track.stop());
      this.outputStream = null;
    }

    // Clean up WebGL resources
    if (this.gl) {
      const gl = this.gl;
      if (this.vao) gl.deleteVertexArray(this.vao);
      if (this.positionBuffer) gl.deleteBuffer(this.positionBuffer);
      if (this.texCoordBuffer) gl.deleteBuffer(this.texCoordBuffer);
      if (this.videoTexture) gl.deleteTexture(this.videoTexture);
      if (this.maskTexture) gl.deleteTexture(this.maskTexture);
      if (this.blurTempTexture) gl.deleteTexture(this.blurTempTexture);
      if (this.blurredTexture) gl.deleteTexture(this.blurredTexture);
      if (this.blurTempFBO) gl.deleteFramebuffer(this.blurTempFBO);
      if (this.blurredFBO) gl.deleteFramebuffer(this.blurredFBO);
      if (this.blurHProgram) gl.deleteProgram(this.blurHProgram.program);
      if (this.blurVProgram) gl.deleteProgram(this.blurVProgram.program);
      if (this.compositeProgram) gl.deleteProgram(this.compositeProgram.program);
    }

    this.vao = null;
    this.positionBuffer = null;
    this.texCoordBuffer = null;
    this.videoTexture = null;
    this.maskTexture = null;
    this.blurTempTexture = null;
    this.blurredTexture = null;
    this.blurTempFBO = null;
    this.blurredFBO = null;
    this.blurHProgram = null;
    this.blurVProgram = null;
    this.compositeProgram = null;
    this.gl = null;

    // Remove output canvas from DOM
    if (this.outputCanvas && this.outputCanvas.parentNode) {
      this.outputCanvas.parentNode.removeChild(this.outputCanvas);
    }
    this.outputCanvas = null;
    this.originalStream = null;

    this._state.set('ready');
    console.log('BackgroundBlurService: Processing stopped');
  }

  /**
   * Get original stream
   */
  getOriginalStream(): MediaStream | null {
    return this.originalStream;
  }

  /**
   * Set blur amount (0-50, maps to shader strength 0-5)
   */
  setBlurAmount(amount: number): void {
    this.blurAmount = Math.max(0, Math.min(50, amount)) / 10;
  }

  /**
   * Get blur amount (0-50 scale)
   */
  getBlurAmount(): number {
    return this.blurAmount * 10;
  }

  /**
   * Dispose all resources
   */
  async dispose(): Promise<void> {
    this.stopProcessing();

    if (this.segmenter) {
      this.segmenter.close();
      this.segmenter = null;
    }

    this._state.set('idle');
    console.log('BackgroundBlurService: Disposed');
  }

  /**
   * Check if processing
   */
  isProcessing(): boolean {
    return this._state() === 'processing';
  }

  /**
   * Check if ready
   */
  isReady(): boolean {
    return this._state() === 'ready' || this._state() === 'processing';
  }

  /**
   * Check if the current platform supports background blur properly.
   * Returns false on Chromium/Linux due to a known captureStream() bug.
   */
  static isPlatformSupported(): boolean {
    return !hasKnownCaptureStreamIssue();
  }

  /**
   * Check platform support (instance method)
   */
  isPlatformSupported(): boolean {
    return BackgroundBlurService.isPlatformSupported();
  }
}
